import { z } from 'zod';

/** Forma do envelope standalone; não é a versão global de um documento de cliente. */
export const CAPTURED_FINANCE_SCHEMA_VERSION = 1 as const;
/** Piso histórico do envelope; a promoção canônica é comprovada por provenance imutável no Worker. */
export const CAPTURED_FINANCE_CANONICAL_MIN_VERSION = CAPTURED_FINANCE_SCHEMA_VERSION;
/** Limite de fatos por envelope captured_finance. */
export const MAX_CAPTURED_FINANCIAL_FACTS = 500 as const;
/** Limite próprio do payload serializado, em bytes UTF-8. */
export const MAX_CAPTURED_FINANCE_PAYLOAD_BYTES = 500_000 as const;
/** Limite de um texto capturado que possa ser renderizado literalmente. */
export const MAX_CAPTURED_TEXT_LENGTH = 4_000 as const;
/** Limite lexical de um código de origem. */
export const MAX_CAPTURED_SOURCE_CODE_LENGTH = 256 as const;
/** Limite explícito para um caminho AST de origem. */
export const MAX_CAPTURED_SOURCE_PATH_LENGTH = 256 as const;

const MAX_SAFE_INTEGER = Number.MAX_SAFE_INTEGER;
const UNSAFE_PATH_IDENTIFIERS = new Set(['__proto__', 'constructor', 'prototype']);
const SOURCE_PATH_PATTERN = /^detalhe\.[A-Za-z_$][A-Za-z0-9_$]*(?:\[(?:0|[1-9]\d*)\])?(?:\.[A-Za-z_$][A-Za-z0-9_$]*(?:\[(?:0|[1-9]\d*)\])?)*$/;
const SOURCE_REPORTED_DECIMAL_PATTERN = /^-?\d{1,16}(?:\.\d{1,18})?$/;
const CAPTURED_FINANCE_SOURCE_ROOT_BY_ROLE = {
  asset: 'patrimonioImobilizado',
  resource: 'recursosFinanceiros',
  income: 'receitaMensal',
  expense: 'diaDia',
} as const;
type CapturedFinanceRole = keyof typeof CAPTURED_FINANCE_SOURCE_ROOT_BY_ROLE;

const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/, 'Hash deve ser SHA-256 hexadecimal minúsculo');
const safePositiveIntegerSchema = z.number()
  .int()
  .positive()
  .max(MAX_SAFE_INTEGER)
  .refine(Number.isSafeInteger, 'Inteiro deve ser representável com segurança');
const safeNonNegativeIntegerSchema = z.number()
  .int()
  .nonnegative()
  .max(MAX_SAFE_INTEGER)
  .refine(Number.isSafeInteger, 'Inteiro deve ser representável com segurança');

function hasUnsafeControl(value: string): boolean {
  return Array.from(value).some((character) => {
    const code = character.codePointAt(0) ?? 0;
    return code <= 0x08 || code === 0x0B || code === 0x0C || (code >= 0x0E && code <= 0x1F) || code === 0x7F;
  });
}

function isSafeSourcePath(value: string): boolean {
  if (!SOURCE_PATH_PATTERN.test(value)) return false;
  const identifiers = value.match(/[A-Za-z_$][A-Za-z0-9_$]*/g) ?? [];
  return identifiers.every((identifier) => !UNSAFE_PATH_IDENTIFIERS.has(identifier));
}

function hasExpectedSourceRoot(role: CapturedFinanceRole, sourcePath: string): boolean {
  const root = /^detalhe\.([A-Za-z_$][A-Za-z0-9_$]*)/.exec(sourcePath)?.[1];
  return root === CAPTURED_FINANCE_SOURCE_ROOT_BY_ROLE[role];
}

function roleSourceRootRefinement(role: CapturedFinanceRole) {
  return (value: { sourceRef: { sourcePath: string } }) => hasExpectedSourceRoot(role, value.sourceRef.sourcePath);
}

/** Texto curto preservado como literal, sem controles não renderizáveis ou markup executável. */
export const capturedTextSchema = z.string()
  .min(1)
  .max(MAX_CAPTURED_TEXT_LENGTH)
  .refine((value) => !hasUnsafeControl(value), 'Texto capturado contém caractere de controle')
  .refine((value) => !/<\/?[A-Za-z][^>]*>|(?:java|vb)script\s*:/i.test(value), 'Texto capturado não pode conter HTML ou script');
export type CapturedText = z.infer<typeof capturedTextSchema>;

/** Código de origem: string lexical ou inteiro seguro; booleanos e floats não são códigos. */
export const sourceCodeValueSchema = z.union([
  z.string()
    .min(1)
    .max(MAX_CAPTURED_SOURCE_CODE_LENGTH)
    .refine((value) => !hasUnsafeControl(value), 'Código de origem contém caractere de controle'),
  z.number().int().refine(Number.isSafeInteger, 'Código numérico deve ser inteiro seguro'),
]);
export type SourceCode = z.infer<typeof sourceCodeValueSchema>;

/** Montante literal da fonte; não é dinheiro canônico e não é convertido pelo contrato. */
export const sourceReportedMoneySchema = z.strictObject({
  amount: z.string()
    .max(64)
    .regex(SOURCE_REPORTED_DECIMAL_PATTERN, 'Montante deve ser literal decimal sem float, expoente ou arredondamento'),
  currency: z.literal('BRL'),
  scale: z.literal('unit'),
  meaning: z.literal('source-reported-amount'),
});
export type SourceReportedMoney = z.infer<typeof sourceReportedMoneySchema>;

export const capturedFieldNotInformedReasonSchema = z.enum(['absent', 'explicit-null', 'empty']);
export type CapturedFieldNotInformedReason = z.infer<typeof capturedFieldNotInformedReasonSchema>;

export const capturedFieldPendingValidationReasonSchema = z.enum([
  'unsupported-type',
  'precision-unsupported',
  'negative-not-supported',
  'alias-conflict',
  'currency-conflict',
  'invalid-format',
]);
export type CapturedFieldPendingValidationReason = z.infer<typeof capturedFieldPendingValidationReasonSchema>;

export const capturedFieldPresentSchema = <T extends z.ZodTypeAny>(valueSchema: T) => z.strictObject({
  state: z.literal('present'),
  value: valueSchema,
});

export const capturedFieldNotInformedSchema = z.strictObject({
  state: z.literal('not_informed'),
  reason: capturedFieldNotInformedReasonSchema,
});

export const capturedFieldPendingValidationSchema = z.strictObject({
  state: z.literal('pending_validation'),
  reason: capturedFieldPendingValidationReasonSchema,
});

/**
 * Fábrica dos três estados de um campo capturado. O estado `present` exige o
 * schema exato do valor; os outros estados não aceitam valor inline.
 */
export function capturedFieldSchema<T extends z.ZodTypeAny>(valueSchema: T) {
  return z.discriminatedUnion('state', [
    capturedFieldPresentSchema(valueSchema),
    capturedFieldNotInformedSchema,
    capturedFieldPendingValidationSchema,
  ]);
}

export type CapturedField<T> =
  | { state: 'present'; value: T }
  | { state: 'not_informed'; reason: CapturedFieldNotInformedReason }
  | { state: 'pending_validation'; reason: CapturedFieldPendingValidationReason };

export const capturedMoneyFieldSchema = capturedFieldSchema(sourceReportedMoneySchema);
export type CapturedMoneyField = z.infer<typeof capturedMoneyFieldSchema>;
export const capturedTextFieldSchema = capturedFieldSchema(capturedTextSchema);
export type CapturedTextField = z.infer<typeof capturedTextFieldSchema>;
export const capturedSourceCodeFieldSchema = capturedFieldSchema(sourceCodeValueSchema);
export type CapturedSourceCodeField = z.infer<typeof capturedSourceCodeFieldSchema>;

/** Caminho fechado da AST de origem; não aceita propriedades perigosas ou mapas arbitrários. */
export const capturedSourcePathSchema = z.string()
  .min(1)
  .max(MAX_CAPTURED_SOURCE_PATH_LENGTH)
  .refine(isSafeSourcePath, 'Caminho deve seguir detalhe.<ident>...[indices] sem propriedades perigosas');

export const capturedSourceRefSchema = z.strictObject({
  captureSha256: sha256Schema,
  sourcePath: capturedSourcePathSchema,
  position: safeNonNegativeIntegerSchema,
});
export type CapturedSourceRef = z.infer<typeof capturedSourceRefSchema>;

const capturedFactIdentityShape = {
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  sourceRef: capturedSourceRefSchema,
};

/** Fato patrimonial capturado: description/sourceCode continuam dados da origem, não categoria. */
export const capturedAssetFactSchema = z.strictObject({
  ...capturedFactIdentityShape,
  role: z.literal('asset'),
  amount: capturedMoneyFieldSchema,
  description: capturedTextFieldSchema,
  sourceCode: capturedSourceCodeFieldSchema,
}).refine(roleSourceRootRefinement('asset'), {
  path: ['sourceRef', 'sourcePath'],
  message: 'Fato asset deve apontar para detalhe.patrimonioImobilizado',
});
export type CapturedAssetFact = z.infer<typeof capturedAssetFactSchema>;

/** Recurso capturado: institutionLabel é permitido somente nesta variante. */
export const capturedResourceFactSchema = z.strictObject({
  ...capturedFactIdentityShape,
  role: z.literal('resource'),
  amount: capturedMoneyFieldSchema,
  institutionLabel: capturedTextFieldSchema,
  sourceCode: capturedSourceCodeFieldSchema,
}).refine(roleSourceRootRefinement('resource'), {
  path: ['sourceRef', 'sourcePath'],
  message: 'Fato resource deve apontar para detalhe.recursosFinanceiros',
});
export type CapturedResourceFact = z.infer<typeof capturedResourceFactSchema>;

/** Receita capturada, sem afirmar periodicidade ou equivalência a monthlyValue. */
export const capturedIncomeFactSchema = z.strictObject({
  ...capturedFactIdentityShape,
  role: z.literal('income'),
  amount: capturedMoneyFieldSchema,
  description: capturedTextFieldSchema,
  sourceCode: capturedSourceCodeFieldSchema,
}).refine(roleSourceRootRefinement('income'), {
  path: ['sourceRef', 'sourcePath'],
  message: 'Fato income deve apontar para detalhe.receitaMensal',
});
export type CapturedIncomeFact = z.infer<typeof capturedIncomeFactSchema>;

/** Despesa capturada, sem afirmar periodicidade ou equivalência a monthlyValue. */
export const capturedExpenseFactSchema = z.strictObject({
  ...capturedFactIdentityShape,
  role: z.literal('expense'),
  amount: capturedMoneyFieldSchema,
  description: capturedTextFieldSchema,
  sourceCode: capturedSourceCodeFieldSchema,
}).refine(roleSourceRootRefinement('expense'), {
  path: ['sourceRef', 'sourcePath'],
  message: 'Fato expense deve apontar para detalhe.diaDia',
});
export type CapturedExpenseFact = z.infer<typeof capturedExpenseFactSchema>;

/** União fechada: o papel determina exatamente os campos aceitos. */
export const capturedFinancialFactSchema = z.discriminatedUnion('role', [
  capturedAssetFactSchema,
  capturedResourceFactSchema,
  capturedIncomeFactSchema,
  capturedExpenseFactSchema,
]);
export type CapturedFinancialFact = z.infer<typeof capturedFinancialFactSchema>;

function duplicateSourceKey(fact: CapturedFinancialFact): string {
  return [fact.role, fact.sourceRef.captureSha256, fact.sourceRef.sourcePath, fact.sourceRef.position].join('\u0000');
}

/**
 * Envelope standalone v1. A chave é uma extensão opcional futura do cliente;
 * este schema deliberadamente não altera `planning_data` nem `embedded-v2`.
 */
export const capturedFinanceDataSchema = z.strictObject({
  schemaVersion: z.literal(CAPTURED_FINANCE_SCHEMA_VERSION),
  version: safePositiveIntegerSchema,
  clientId: z.string().uuid(),
  moneyPolicySha256: sha256Schema,
  facts: z.array(capturedFinancialFactSchema)
    .min(1, 'Envelope presente deve conter ao menos um fato')
    .max(MAX_CAPTURED_FINANCIAL_FACTS),
}).superRefine((value, context) => {
  const owner = value.clientId.toLowerCase();
  const ids = new Set<string>();
  const sourceRefs = new Set<string>();

  value.facts.forEach((fact, index) => {
    if (fact.clientId.toLowerCase() !== owner) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['facts', index, 'clientId'],
        message: 'Fato captured_finance deve pertencer ao mesmo clientId do envelope',
      });
    }

    const id = fact.id.toLowerCase();
    if (ids.has(id)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['facts', index, 'id'],
        message: 'Fatos captured_finance não podem duplicar UUID',
      });
    }
    ids.add(id);

    const sourceKey = duplicateSourceKey(fact);
    if (sourceRefs.has(sourceKey)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['facts', index, 'sourceRef'],
        message: 'A mesma captura/path/posição não pode repetir o mesmo papel',
      });
    }
    sourceRefs.add(sourceKey);
  });

  try {
    const serialized = JSON.stringify(value);
    const payloadBytes = new TextEncoder().encode(serialized).byteLength;
    if (payloadBytes > MAX_CAPTURED_FINANCE_PAYLOAD_BYTES) {
      context.addIssue({
        code: z.ZodIssueCode.too_big,
        maximum: MAX_CAPTURED_FINANCE_PAYLOAD_BYTES,
        origin: 'string',
        inclusive: true,
        path: [],
        message: `Payload captured_finance excede ${MAX_CAPTURED_FINANCE_PAYLOAD_BYTES} bytes`,
      });
    }
  } catch {
    context.addIssue({ code: z.ZodIssueCode.custom, path: [], message: 'Payload captured_finance não é serializável' });
  }
});
export type CapturedFinanceData = z.infer<typeof capturedFinanceDataSchema>;
